import { IonImg, useIonToast } from '@ionic/react';
import {
  FilterTypeEnum,
  getTimeFrameInfo,
  IFilterType,
  OrderByEnum,
  TimeFrameValueEnum,
} from 'common/common';
import { TimeFrameValue } from 'common/types';
import { config } from 'config/config';
import { CasinoService, IRequestCasinoParams, IRequestDetailParam } from 'services/casino';
import { useCallback, useEffect, useState } from 'react';
import {
  ICasinoReport,
  IGraphGameData,
  ISlotBucketData,
  ISlotGraphData,
} from 'appRedux/models/casinoModels';
import { useDispatch, useSelector } from 'react-redux';
import { IState, ITabProperties } from 'appRedux/createStore';
import { Insights, InsightValueEnum } from 'config/Insights';
import moment from 'moment';
import { debounce } from 'lodash';
import DropdownBar from 'components/DropdownBar/DropdownBar';
import TimeRangeButtons from './TimeRangeButtons';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import { defaultTimeRanges, TimeRangeOption } from 'config/Timeframes';
import AnnotatedChart from './Charts/AnnotatedChart';
import GraphTabs, { graphTabs } from './Charts/GraphTabs';
import { FilterReduxCommandCreator } from 'appRedux/actions/filterCommandCreator';
import SectionHeader from './SectionHeader';
import { a } from 'react-spring';
import { isInsightCasinoSchema } from 'services/insightCasino';
import { getEgmDetailGraphDefaultTimeframe } from 'lib/applicationConfig';

interface GraphComponentProps {
  timeRanges?: TimeRangeOption[];
  activeRange?: TimeFrameValue;
  isProUser: boolean;
  playData: ICasinoReport;
  onTimeRangeChange?: (range: TimeFrameValue) => void;
  onBack?: () => void;
  onClickUpgradeNow: () => void;
}

enum selectedItem {
  Unknown = 'Unknown',
}

const hourlyRangeHoursMap: Record<string, number> = {
  [TimeFrameValueEnum.hours1]: 1,
  [TimeFrameValueEnum.hours4]: 4,
  [TimeFrameValueEnum.hours8]: 8,
  [TimeFrameValueEnum.hours24]: 24,
};

const GraphComponent: React.FC<GraphComponentProps> = ({
  timeRanges = defaultTimeRanges,
  // applicationConfigurations egmDetailGraphDefaultTimeframe; fallback months1.
  activeRange = getEgmDetailGraphDefaultTimeframe() as TimeFrameValue,
  isProUser,
  playData,
  onTimeRangeChange,
  onBack,
  onClickUpgradeNow,
}) => {
  const casinoSvc = new CasinoService();
  const [presentToast] = useIonToast();
  const [collapse, setCollapse] = useState(true);
  const toggleCollapse = () => {
    setCollapse(!collapse);
  };
  const [finalJKPTData, setJKPTData] = useState<IGraphGameData[]>([]);
  const graphFilters = useSelector<IState>(
    (state) => state.app.persistedState.filterState[FilterTypeEnum.graphfilter as IFilterType]
  ) as ITabProperties;
  const { insight } = graphFilters;
  const { activeSlotNumber, clearSlotHistory } = useSlotDetailHistory();
  const filterTime: any = getTimeFrameInfo(activeRange);
  const insightId = Insights.find((eachInsight) => eachInsight.value === insight)?.insightId;
  const InsightData = Insights.find((eachInsight) => eachInsight.value === insight);
  const IsInsightHaveValue = Insights.filter(
    (insight) => insight.enableGraphPulldown || insight.enableGraphButton
  );
  const IsInsightHaveValueGraphPulldown = Insights.filter((insight) => insight.enableGraphPulldown);
  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;
  // Keyed off the selected casino object itself (insightInstance), not an id.
  const isInsightSchema = useSelector((state: IState) =>
    isInsightCasinoSchema(state.app.persistedState.casinoSchema)
  );
  const [rawGraphData, setRawGraphData] = useState<ISlotGraphData[] | undefined>();
  const [graphData, setGraphData] = useState<ISlotGraphData[]>();
  const [bucketData, setBucketData] = useState<ISlotBucketData>();
  const [isSlotDataLoading, setIsSlotDataLoading] = useState(false);
  const [isSlotGraphDataLoading, setSlotGraphDataLoading] = useState(false);
  const [slotSplitData, setslotSplitData] = useState<IGraphGameData[]>([]);
  const dispatch = useDispatch();
  const filterCommandCreator = FilterReduxCommandCreator(dispatch);
  const [casinoParams, setCasinoParams] = useState<IRequestDetailParam>({
    casinofk: casinoId,
    timeframe: 6,
    insight: InsightValueEnum.spins,
    slot: 0,
    orderby: OrderByEnum.Desc,
  });
  const [selectedTabId, setSelectedTabId] = useState<string>(selectedItem.Unknown);
  const currentTab = graphTabs.find((tab) => tab.id === selectedTabId);

  const handleInputChange = (param: keyof IRequestCasinoParams, value: string | number) => {
    setCasinoParams((prevParams) => ({ ...prevParams, [param]: value }));
  };

  const fetchGraphBucketSlotReport = useCallback(
    (queryParams) => {
      setIsSlotDataLoading(true);
      casinoSvc.GetGraphBucketSlotReport(queryParams).subscribe({
        next(casinoResponse) {
          if (casinoResponse) {
            setBucketData(casinoResponse.totalJackpots?.[0] || []);
            setRawGraphData(casinoResponse.slotPlayData);
          }
        },
        error() {
          setIsSlotDataLoading(false);
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Failed to retrieve casino report.',
          }).catch(() => null);
        },
        complete() {
          setIsSlotDataLoading(false);
        },
      });
    },
    [casinoSvc]
  );

  const fetchGetGraphData = useCallback(
    (queryParams) => {
      setSlotGraphDataLoading(true);
      casinoSvc.GetGraphData(queryParams).subscribe({
        next(casinoResponse) {
          if (casinoResponse) {
            setslotSplitData(casinoResponse.DataSet);
          }
        },
        error() {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Failed to retrieve casino report.',
          }).catch(() => null);
        },
        complete() {
          setSlotGraphDataLoading(false);
        },
      });
    },
    [casinoSvc]
  );

  const requestGraphData = useCallback(
    (requestParams) => {
      fetchGetGraphData(requestParams);
    },
    [fetchGetGraphData]
  );

  useEffect(() => {
    if (bucketData && playData) {
      // Bar drill-down (GetGraphData) has no casinoInsight equivalent: disabled
      // for insight casinos, whose bars come from GetCasinoInsightGraph.
      if (bucketData.bucket && insightId && !isInsightSchema) {
        const requestParams = {
          slot_number: activeSlotNumber,
          insight_filter: insightId,
          timeframe_filter: filterTime?.id,
          timebucket_id: filterTime?.graphtimeBucket,
          bucket_time: bucketData.bucket,
          casinofk: casinoId,
        };
        requestGraphData(requestParams);
      }
    }
  }, [bucketData]);

  useEffect(() => {
    if (graphData) {
      const finalData = slotSplitData.map((element) => {
        const gameBucketTime = element.game_time;

        const matchedData = graphData.find((data) => {
          const sourceBucketTime = data?.bucket;
          const dateBucket = moment(sourceBucketTime)
            .add(1, 'd')
            .subtract(1, 'milliseconds')
            .format();
          return moment(gameBucketTime).isBetween(sourceBucketTime, dateBucket);
        });

        return matchedData ? { ...element, ...matchedData } : element;
      });

      setJKPTData(finalData);
    }
  }, [graphData, slotSplitData]);

  useEffect(() => {
    setIsSlotDataLoading(true);
    const currentTab = graphTabs.find((tab) => tab.id === selectedTabId);
    const fallbackTab = !currentTab ? graphTabs.find((tab) => tab.primary === insight) : currentTab;
    if (fallbackTab && fallbackTab.primary) {
      setSelectedTabId(fallbackTab.id);
    } else {
      setSelectedTabId(selectedItem.Unknown);
    }
  }, [insight]);

  const debouncedGetSlotDataReport = useCallback(
    debounce((params) => {
      fetchGraphBucketSlotReport(params);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedGetSlotDataReport(casinoParams);
    return () => {
      debouncedGetSlotDataReport.cancel();
    };
  }, [casinoParams, debouncedGetSlotDataReport]);

  useEffect(() => {
    if (!rawGraphData) {
      setGraphData(undefined);
      return;
    }

    if (rawGraphData.length === 0) {
      setGraphData(rawGraphData);
      return;
    }

    const zeroRow = (bucket: string): ISlotGraphData =>
      ({
        bucket,
        game_name: rawGraphData[0].game_name,
        slot_number: rawGraphData[0].slot_number,
        jackpots: 0, jackpots_ntile: 0,
        jackpot_value: 0, jackpot_value_ntile: 0,
        money_played: 0, money_played_ntile: 0,
        money_won: 0, money_won_ntile: 0,
        wins: 0, wins_ntile: 0,
        spins: 0, spins_ntile: 0,
        return: 0, return_ntile: 0,
      } as ISlotGraphData);

    const hours = hourlyRangeHoursMap[activeRange];
    if (hours) {
      // Hourly timeframes: exact last N closed hours ending at the previous hour
      // (anchor to now — never to the latest data point, which stretched short windows).
      const bucketMap = new Map<string, ISlotGraphData>();
      rawGraphData.forEach((d) => {
        const hour = moment.utc(d.bucket).startOf('hour').toISOString();
        const existing = bucketMap.get(hour);
        if (!existing) {
          bucketMap.set(hour, d);
        } else {
          const prevVal = Number(existing.money_played) || 0;
          const currVal = Number(d.money_played) || 0;
          if (currVal > prevVal) bucketMap.set(hour, d);
        }
      });
      const end = moment.utc().startOf('hour').subtract(1, 'hour');
      const result: ISlotGraphData[] = [];
      for (let i = hours - 1; i >= 0; i--) {
        const h = end.clone().subtract(i, 'hours').startOf('hour').toISOString();
        const row = bucketMap.get(h);
        result.push(row ? { ...row, bucket: h } : zeroRow(h));
      }
      setGraphData(result);
      return;
    }

    const dailyDaysMap: Record<string, number> = {
      [TimeFrameValueEnum.weeks1]: 7,
      [TimeFrameValueEnum.months1]: 30,
    };
    const days = dailyDaysMap[activeRange];
    if (days) {
      // Daily: last N days. 7d = completed days before today; 30d includes today.
      const dayMap = new Map<string, ISlotGraphData>();
      rawGraphData.forEach((d) => {
        const day = moment.utc(d.bucket).startOf('day').toISOString();
        const existing = dayMap.get(day);
        if (!existing) {
          dayMap.set(day, d);
        } else {
          const prevVal = Number(existing.money_played) || 0;
          const currVal = Number(d.money_played) || 0;
          if (currVal > prevVal) dayMap.set(day, d);
        }
      });
      const includeToday = activeRange === TimeFrameValueEnum.months1;
      const end = includeToday
        ? moment.utc().startOf('day')
        : moment.utc().startOf('day').subtract(1, 'day');
      const result: ISlotGraphData[] = [];
      for (let i = days - 1; i >= 0; i--) {
        const d = end.clone().subtract(i, 'days').startOf('day').toISOString();
        const row = dayMap.get(d);
        result.push(row ? { ...row, bucket: d } : zeroRow(d));
      }
      setGraphData(result);
      return;
    }

    // Fallback: use raw data as-is
    setGraphData(rawGraphData);
  }, [rawGraphData, activeRange]);

  useEffect(() => {
    handleInputChange('insight', insight);
  }, [insight, selectedTabId]);

  useEffect(() => {
    handleInputChange('slot', activeSlotNumber);
  }, [activeSlotNumber]);

  useEffect(() => {
    const filterTime = config.allTimeFrames.find((item) => item.value === activeRange);
    if (filterTime) {
      handleInputChange('timeframe', filterTime.id);
    }
  }, [activeRange]);

  return (
    <div className="slot-panel slot-graph">
      <div className="graph-section-area">
        {IsInsightHaveValue.length > 0 ? (
          <>
            {IsInsightHaveValueGraphPulldown.length > 0 && (
              <DropdownBar
                id={FilterTypeEnum.graphfilter}
                isShowShortName
                showOnlyInsightSelector
                onChangeValue={() => console.log('insight change')}
              />
            )}

            <div className="graph-insights">
              <SectionHeader title="Graphs" rotateDisabled={true} iconName="graph-section-trends" />
              <GraphTabs
                tabs={graphTabs}
                activeTabId={selectedTabId}
                onSelect={(item) => {
                  const currentTab = graphTabs.find((tab) => tab.id === item);
                  if (currentTab) {
                    filterCommandCreator.SetFilterOptions(FilterTypeEnum.graphfilter, {
                      insight: currentTab.primary,
                    });
                  }
                  setSelectedTabId(item);
                }}
                isPro={isProUser}
              />
              <hr
                style={{
                  color: '#a0a0a0',
                  background: '#a0a0a0',
                  height: 1,
                  width: '95%',
                }}
              />
              <TimeRangeButtons
                timeRanges={timeRanges}
                activeRange={activeRange}
                isProUser={isProUser}
                onTimeRangeChange={(val) => onTimeRangeChange?.(val)}
              />
            </div>

            {InsightData?.enableGraph ? (
              <div className="slot-chart-box-new">
                <div className="total-insight-value">
                  <div className="graph-description">
                    <span>{currentTab?.graphDescription}</span>
                  </div>
                  <AnnotatedChart
                    playData={playData}
                    selectedTabId={selectedTabId}
                    timeframe={activeRange}
                    insight={insight}
                    graphData={graphData}
                    slotSplitData={finalJKPTData}
                    onClickUpgradeNow={onClickUpgradeNow}
                    isLoading={isSlotGraphDataLoading || isSlotDataLoading}
                    bucketData={bucketData}
                  />
                </div>
              </div>
            ) : (
              !graphData &&
              !isSlotDataLoading && (
                <div className="slot-chart-box-new no-graph">
                  <div className="total-insight-value">
                    <div className="no-jackpots-container slot-chart-header graph-overlay">
                      Graph display not available for this insight.
                    </div>
                  </div>
                </div>
              )
            )}
          </>
        ) : (
          <div className="slot-chart-box-new no-graph">
            <div className="total-insight-value">
              <div className="no-jackpots-container slot-chart-header graph-overlay">
                Graph is currently unavailable. Please contact our support team to enable graph
                access for your insights.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GraphComponent;
